import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ButtonDestinationEditor } from "../inspector/LandingInspectorControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { buildInternalDestinationOptions, getValueAtDescriptorPath } from "../elementEditing";

function FieldControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return <FormControlLabel control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />} label={field.label} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <TextField fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return <TextField fullWidth size="small" label={field.label} value={value ?? ""} disabled={disabled} placeholder="#000000 o transparent" onChange={(event) => onCommit?.(field.path, event.target.value)} />;
  }
  return <TextField fullWidth size="small" label={field.label} value={value ?? ""} disabled={disabled} multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA} minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined} inputProps={{ maxLength: field.maxLength }} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
}

export default function ButtonElementEditor({ descriptor, form, disabled, onCommit }) {
  const labelField = descriptor?.labelField;
  if (!labelField) return null;
  const labelValue = getValueAtDescriptorPath(form, labelField.path, "");
  const options = buildInternalDestinationOptions(form);
  const showField = descriptor?.showField;
  const requiresField = descriptor?.requiresProductsField || (descriptor?.extraFields || []).find((field) => /RequiresProducts$/.test(field?.path || ""));
  const remainingExtra = (descriptor?.extraFields || []).filter((field) => field !== requiresField);
  const styleFields = descriptor?.styleFields || [];
  const showIconField = descriptor?.showIconField;
  const iconField = descriptor?.iconField;
  const urlField = descriptor?.urlField;
  const behaviorField = descriptor?.behaviorField;
  const targetField = descriptor?.targetField;
  const sharedField = [labelField, urlField, showField, requiresField, behaviorField, targetField, ...remainingExtra]
    .find((field) => field?.shared);

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
        {(descriptor?.chips || []).map((chip) => <Chip key={chip} size="small" label={chip} variant="outlined" />)}
        {sharedField ? <Chip size="small" label="Se usa también en otras secciones" variant="outlined" /> : null}
        {requiresField && getValueAtDescriptorPath(form, requiresField.path, false) ? <Chip size="small" label="Catálogo" /> : null}
        {showIconField && getValueAtDescriptorPath(form, showIconField.path, false) ? <Chip size="small" label="Con icono" /> : null}
      </Stack>

      <Box sx={{ p: 1.2, borderRadius: 2.5, border: "1px solid rgba(25,36,47,.1)", bgcolor: "rgba(25,36,47,.025)" }}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Vista previa</Typography>
        <Box sx={{ mt: 0.8, display: "inline-flex", px: 2, py: 1, borderRadius: 999, bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 900 }}>
          {labelValue || "Botón"}
        </Box>
      </Box>

      <TextField fullWidth size="small" label={labelField.label || "Texto del botón"} value={labelValue} disabled={disabled} inputProps={{ maxLength: labelField.maxLength }} onChange={(event) => onCommit?.(labelField.path, event.target.value)} />

      {showField ? <FieldControl field={showField} form={form} disabled={disabled} onCommit={onCommit} /> : null}
      {behaviorField ? <FieldControl field={behaviorField} form={form} disabled={disabled} onCommit={onCommit} /> : null}
      {targetField ? <FieldControl field={targetField} form={form} disabled={disabled} onCommit={onCommit} /> : null}

      {urlField ? (
        urlField.destinationPicker ? (
          <ButtonDestinationEditor field={urlField} value={getValueAtDescriptorPath(form, urlField.path, "")} options={options} disabled={disabled} onCommit={onCommit} />
        ) : <FieldControl field={urlField} form={form} disabled={disabled} onCommit={onCommit} />
      ) : (!targetField ? <Chip size="small" label="Destino definido por esta sección" variant="outlined" sx={{ alignSelf: "flex-start" }} /> : null)}

      {requiresField ? <FieldControl field={requiresField} form={form} disabled={disabled} onCommit={onCommit} /> : null}
      {remainingExtra.map((field) => <FieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />)}

      {showIconField || iconField ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Icono</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{showIconField ? <FieldControl field={showIconField} form={form} disabled={disabled} onCommit={onCommit} /> : null}{iconField ? <FieldControl field={iconField} form={form} disabled={disabled} onCommit={onCommit} /> : null}</Stack></AccordionDetails>
        </Accordion>
      ) : null}

      {(descriptor?.megaMenu || (descriptor?.megaMenuFields || []).length || (descriptor?.megaMenuOverrideFields || []).length) ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Configurar mega menú</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {(descriptor?.megaMenuOverrideFields || []).map((field) => <FieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />)}
              {(descriptor?.megaMenuFields || []).map((field) => <FieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {styleFields.length ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia y hover</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((field) => <FieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
